/**
 * Band — the §7.5 inline band (mission 6 WP03 T022; FR-023, C-003; B.3, B.5,
 * D.2).
 *
 * Info, warning, blocked or success, on `ui/alert.tsx`: a 20px lucide icon in
 * the family's 700 token, 14/400 text, an optional trailing action (a link or
 * an sm button). Blocked is `role="alert"`, the others `role="status"`.
 * `requirement` becomes `data-requirement` (a key for tests and the harness,
 * not visible text). Never dismissible, never shadowed. The disclaimer band
 * (T.2, T.5, SAT.7) is `DisclaimerBand`, not a kind here.
 */
import type { HTMLAttributes, ReactNode } from 'react'
import { CircleCheck, Info, OctagonAlert, TriangleAlert, type LucideIcon } from 'lucide-react'

import { Alert } from '@/components/ui/alert'
import { cn } from '@/components/ui/cn'

export type BandKind = 'info' | 'warning' | 'blocked' | 'success'

const ICONS: Record<BandKind, { icon: LucideIcon; color: string }> = {
  info: { icon: Info, color: 'text-[var(--color-info-700)]' },
  warning: { icon: TriangleAlert, color: 'text-[var(--color-warning-700)]' },
  blocked: { icon: OctagonAlert, color: 'text-[var(--color-error-700)]' },
  success: { icon: CircleCheck, color: 'text-[var(--color-success-700)]' },
}

export interface BandProps extends Omit<HTMLAttributes<HTMLDivElement>, 'role'> {
  kind: BandKind
  /** A trailing link or sm button. */
  action?: ReactNode
  /** The requirement the band implements (B.3, D.2, …), as `data-requirement`. */
  requirement?: string | null | undefined
}

export function Band({ kind, action, requirement, className, children, ...props }: BandProps) {
  const { icon: Icon, color } = ICONS[kind]
  return (
    <Alert
      variant={kind}
      data-band-kind={kind}
      data-requirement={requirement ?? undefined}
      className={className}
      {...props}
    >
      <Icon aria-hidden className={cn('size-5', color)} />
      <div className="min-w-0 flex-1">{children}</div>
      {action ? <div className="shrink-0 self-center">{action}</div> : null}
    </Alert>
  )
}
